/**
 * JointDiagram — tiny inline SVG body silhouette with one joint highlighted.
 * Usage: <JointDiagram joint="elbow" />
 *
 * Mapping is based on the word closest to common joint names. We do string match
 * against the angle label so exercise-agnostic labels like "Elbow Bend / Press"
 * or "Torso Control (no big forward lean)" still resolve.
 */
const JOINT_POINTS = {
  elbow:    { cx: 16, cy: 30, label: 'Elbow' },
  shoulder: { cx: 20, cy: 19, label: 'Shoulder' },
  wrist:    { cx: 13, cy: 40, label: 'Wrist' },
  knee:     { cx: 26, cy: 56, label: 'Knee' },
  hip:      { cx: 26, cy: 40, label: 'Hip' },
  ankle:    { cx: 26, cy: 72, label: 'Ankle' },
  torso:    { cx: 26, cy: 32, label: 'Torso' },
  body:     { cx: 26, cy: 32, label: 'Body' },
};

// Keyword-first matching so AI-generated labels resolve reliably.
const resolveJoint = (label) => {
  if (!label) return null;
  const l = label.toLowerCase();
  if (l.includes('elbow')) return 'elbow';
  if (l.includes('shoulder')) return 'shoulder';
  if (l.includes('wrist')) return 'wrist';
  if (l.includes('knee')) return 'knee';
  if (l.includes('hip')) return 'hip';
  if (l.includes('ankle')) return 'ankle';
  if (l.includes('torso') || l.includes('trunk') || l.includes('lean')) return 'torso';
  if (l.includes('body') || l.includes('spine') || l.includes('plank') || l.includes('line')) return 'body';
  return null;
};

export default function JointDiagram({ label, size = 48 }) {
  const key = resolveJoint(label);
  const point = key ? JOINT_POINTS[key] : null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 52 92"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={point ? `${point.label} diagram` : 'Body diagram'}
    >
      {/* Stick figure — head */}
      <circle cx="26" cy="9" r="6" stroke="#52525b" strokeWidth="1.5" fill="none" />
      {/* Torso */}
      <line x1="26" y1="16" x2="26" y2="46" stroke="#52525b" strokeWidth="1.5" />
      {/* Shoulders */}
      <line x1="14" y1="19" x2="38" y2="19" stroke="#52525b" strokeWidth="1.5" />
      {/* Left arm (upper + lower) */}
      <line x1="14" y1="19" x2="10" y2="30" stroke="#52525b" strokeWidth="1.5" />
      <line x1="10" y1="30" x2="13" y2="42" stroke="#52525b" strokeWidth="1.5" />
      {/* Right arm */}
      <line x1="38" y1="19" x2="42" y2="30" stroke="#52525b" strokeWidth="1.5" />
      <line x1="42" y1="30" x2="39" y2="42" stroke="#52525b" strokeWidth="1.5" />
      {/* Hips to feet — left */}
      <line x1="26" y1="46" x2="20" y2="62" stroke="#52525b" strokeWidth="1.5" />
      <line x1="20" y1="62" x2="18" y2="78" stroke="#52525b" strokeWidth="1.5" />
      {/* Hips to feet — right */}
      <line x1="26" y1="46" x2="32" y2="62" stroke="#52525b" strokeWidth="1.5" />
      <line x1="32" y1="62" x2="34" y2="78" stroke="#52525b" strokeWidth="1.5" />

      {/* Highlighted joint marker — yellow accent dot with subtle halo */}
      {point && (
        <>
          <circle cx={point.cx} cy={point.cy} r="6" fill="#CCFF00" opacity="0.25" />
          <circle cx={point.cx} cy={point.cy} r="3" fill="#CCFF00" />
        </>
      )}
    </svg>
  );
}
